import React, { useState } from 'react';
import './Profile.css';
import useAuth from '../hooks/useAuth.ts';
import { toast } from '../utils/sweetAlert.ts';
import { changePassword } from '../services/apiUsers.ts';

const Profile: React.FC = () => {
  const { auth } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: auth.user?.name || '',
    email: auth.user?.email || '',
    role: auth.user?.role || 'user',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form
    if (formData.newPassword && formData.newPassword !== formData.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    
    try {
      // If user wants to change password, validate current password is provided
      if (formData.newPassword) {
        if (!formData.currentPassword) {
          toast.error('Current password is required to change password');
          return;
        }
        
        if (formData.newPassword.length < 8) {
          toast.error('New password must be at least 8 characters long');
          return;
        }
        
        // Call the change password API
        await changePassword(formData.currentPassword, formData.newPassword);
        toast.success('Password changed successfully');
        
        // Clear password fields
        setFormData(prev => ({
          ...prev,
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        }));
      } else {
        // For now, just show success for profile updates without password change
        // In a real app, you would make an API call here to update the profile
        toast.success('Profile updated successfully');
      }
      
      setIsEditing(false);
    } catch (err: any) {
      console.error('Profile update error:', err);
      
      // Handle different error scenarios
      if (err.response?.status === 401) {
        toast.error('Current password is incorrect');
      } else if (err.response?.data?.message) {
        toast.error(err.response.data.message);
      } else {
        toast.error('An error occurred while updating your profile');
      }
    }
  };

  return (
    <div className="profile-container">
      <div className="profile-header">
        <h1>My Profile</h1>
        {!isEditing && (
          <button 
            className="edit-button"
            onClick={() => setIsEditing(true)}
          >
            Edit Profile
          </button>
        )}
      </div>
      
      <div className="profile-card">
        {isEditing ? (
          <form onSubmit={handleSubmit} className="profile-form">
            <div className="form-group">
              <label htmlFor="name">Name</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="role">Role</label>
              <input
                type="text"
                id="role"
                name="role"
                value={formData.role}
                disabled
              />
              <small className="form-text">Role cannot be changed</small>
            </div>
            
            <div className="password-section">
              <h3>Change Password</h3>
              <div className="form-group">
                <label htmlFor="currentPassword">Current Password</label>
                <input
                  type="password"
                  id="currentPassword"
                  name="currentPassword"
                  value={formData.currentPassword}
                  onChange={handleChange}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="newPassword">New Password</label>
                <input
                  type="password"
                  id="newPassword"
                  name="newPassword"
                  value={formData.newPassword}
                  onChange={handleChange}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="confirmPassword">Confirm New Password</label>
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                />
              </div>
            </div>
            
            <div className="form-actions">
              <button type="submit" className="save-button">Save Changes</button>
              <button 
                type="button" 
                className="cancel-button"
                onClick={() => setIsEditing(false)}
              >
                Cancel
              </button>
            </div>
          </form>
        ) : (
          <div className="profile-details">
            <div className="profile-info">
              <div className="info-group">
                <span className="info-label">Name</span>
                <span className="info-value">{auth.user?.name || 'N/A'}</span>
              </div>
              
              <div className="info-group">
                <span className="info-label">Email</span>
                <span className="info-value">{auth.user?.email || 'N/A'}</span>
              </div>
              
              <div className="info-group">
                <span className="info-label">Role</span>
                <span className="info-value">{auth.user?.role || 'User'}</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Profile;
